// WHAT TO CALL A FACILITY'S IDENTIFIER, WHEN IT IS NOT ALWAYS THE SAME KIND OF THING.
//
// `facility.facility_id` holds one of two different identifiers, and the product has been
// labelling both of them "CCN":
//
//   145312   a CMS Certification Number — six digits, issued to a certified provider
//   12       a PointClickCare facility id — tenant-local, assigned by PCC, not by CMS
//
// A PCC SANDBOX FACILITY IS NOT A CERTIFIED PROVIDER AND HAS NO CCN AT ALL. Printing
// "CCN: 12" on a facility card, an export header or a Word document states something that
// is not true of that facility and could not be looked up anywhere — and a report carrying
// a fabricated CCN is exactly the kind of document that should never leave a building.
//
// The codebase already knows the difference where it matters operationally:
// metrics/data/cmsAdapter.js refuses to call the CMS relay unless the value is six digits,
// because a PCC facId would 404 against Care Compare. That guard protects a network call.
// This protects the sentence a person reads.
//
// ── SIX DIGITS IS A SHAPE, NOT A PROOF ──────────────────────────────────────
//
// A PCC tenant could in principle assign a six-digit facility id, and this would call it a
// CCN. That is a real limit and the reason the neutral label is "Facility ID" rather than
// "PCC ID": claiming the positive ("this is a PointClickCare id") needs evidence this
// function does not have, while declining to claim the negative is free. Where a caller
// knows the facility is PCC-integrated it should say so itself.

/** A CMS Certification Number is six digits. The same shape cmsAdapter.js guards on. */
export function looksLikeCcn(value) {
  return /^\d{6}$/.test(String(value ?? "").trim());
}

/**
 * What to call this identifier in front of a person.
 *
 * Returns "CCN" only when the value could actually be one. Everything else — a PCC facId, a
 * sandbox facility, an empty field — gets the neutral label, because being vague is not a
 * defect and being wrong is.
 */
export function facilityIdLabel(value) {
  return looksLikeCcn(value) ? "CCN" : "Facility ID";
}

/**
 * The identifier as one labelled string: `CCN 145312`, `Facility ID 12`, or a dash.
 *
 * THE DASH IS NOT THE LABEL PLUS A DASH. "CCN —" reads as a facility whose CCN is missing,
 * which is a different and more alarming claim than "we do not have an identifier here".
 */
export function facilityIdText(value, { dash = "—" } = {}) {
  const v = String(value ?? "").trim();
  if (!v) return dash;
  return `${facilityIdLabel(v)} ${v}`;
}

export default facilityIdText;
